import React from 'react';
import { FileText, ShoppingCart, Truck, DollarSign } from 'lucide-react';

// Modal de Detalhes do Pedido (historico) do cardapio publico.
// Extraido VERBATIM de PublicMenu.js (Fase 2 refactor). Componente puro (sem hooks),
// recebe estado/handlers via props. Renderizado como <OrderDetailsModal .../>.
const OrderDetailsModal = ({ selectedOrderDetails, setSelectedOrderDetails }) => {
    if (!selectedOrderDetails) return null;

    const order = selectedOrderDetails;

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatTime = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const getStatusColor = (status) => {
      const colors = {
        'preparando': 'bg-primary',
        'pronto': 'bg-green-500',
        'entregue': 'bg-gray-500',
        'cancelado': 'bg-red-500'
      };
      return colors[status] || 'bg-gray-500';
    };
    
    const getStatusLabel = (status, deliveryType = 'delivery') => {
      // Para status 'entrega', diferenciar entre delivery e retirada
      if (status === 'entrega') {
        if (deliveryType === 'retirada' || deliveryType === 'pickup') {
          return 'Pronto p/ Retirada';
        }
        return 'Saiu p/ Entrega';
      }
      
      // Para status 'pronto', diferenciar entre delivery e retirada
      if (status === 'pronto' || status === 'entregue') {
        if (deliveryType === 'retirada' || deliveryType === 'pickup') {
          return 'Retirado';
        }
        return 'Entregue';
      }
      
      const labels = {
        'pendente': 'Pendente',
        'em_producao': 'Em Produção',
        'preparando': 'Preparando',
        'cancelado': 'Cancelado'
      };
      return labels[status] || status;
    };

    const getDeliveryTypeLabel = (type) => {
      const labels = {
        'delivery': '🚚 Entrega',
        'retirada': '🏪 Retirada',
        'mesa': '🪑 Mesa'
      };
      return labels[type] || type;
    };

    const getPaymentMethodLabel = (method) => {
      const labels = {
        'dinheiro': '💵 Dinheiro',
        'pix': '📱 PIX',
        'pix_mp': '🚀 PIX Automático',
        'cartao': '💳 Cartão',
        'cartao_credito': '💳 Cartão de Crédito',
        'cartao_debito': '💳 Cartão de Débito',
        'vale_refeicao': '🍽️ Vale Refeição',
        'vale_alimentacao': '🛒 Vale Alimentação'
      };
      return labels[method] || method;
    };

    return (
      <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
          onClick={() => setSelectedOrderDetails(null)}
        />
        
        {/* Modal Content */}
        <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-t-3xl sm:rounded-t-2xl z-10">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-3 backdrop-blur-sm">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1">
                Pedido #{order.order_number}
              </h3>
              <p className="text-white text-opacity-90 text-sm">
                {formatDate(order.created_at)} às {formatTime(order.created_at)}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Status Badge */}
            <div className="flex justify-center">
              <span className={`px-4 py-2 rounded-full text-white text-sm font-medium ${getStatusColor(order.status)}`}>
                {getStatusLabel(order.status, order.delivery_type)}
              </span>
            </div>

            {/* Items Section */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <ShoppingCart size={18} />
                Itens do Pedido
              </h4>
              <div className="space-y-3">
                {order.items && order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">
                        {item.quantity}x {item.product_name}
                      </p>
                      {item.additionals && item.additionals.length > 0 && (
                        <div className="mt-1 ml-4 space-y-1">
                          {item.additionals.map((add, addIdx) => (
                            <p key={addIdx} className="text-xs text-gray-600">
                              + {add.quantity}x {add.name} (R$ {add.price?.toFixed(2)})
                            </p>
                          ))}
                        </div>
                      )}
                      {item.notes && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                          Obs: {item.notes}
                        </p>
                      )}
                    </div>
                    <span className="text-gray-700 font-medium ml-2">
                      R$ {((item.price || 0) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery Info */}
            {order.delivery_type && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Truck size={18} />
                  Informações de Entrega
                </h4>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-700">
                    <span className="font-medium">Tipo:</span> {getDeliveryTypeLabel(order.delivery_type)}
                  </p>
                  {order.address && (
                    <>
                      <p className="text-gray-700">
                        <span className="font-medium">Endereço:</span> {order.address.street}, {order.address.number}
                      </p>
                      {order.address.complement && (
                        <p className="text-gray-700">
                          <span className="font-medium">Complemento:</span> {order.address.complement}
                        </p>
                      )}
                      <p className="text-gray-700">
                        <span className="font-medium">Bairro:</span> {order.address.neighborhood}
                      </p>
                      {order.address.city && (
                        <p className="text-gray-700">
                          <span className="font-medium">Cidade:</span> {order.address.city}
                        </p>
                      )}
                    </>
                  )}
                  {order.table_number && (
                    <p className="text-gray-700">
                      <span className="font-medium">Mesa:</span> {order.table_number}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Payment Info */}
            {order.payment_method && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <DollarSign size={18} />
                  Pagamento
                </h4>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-700">
                    <span className="font-medium">Forma:</span> {getPaymentMethodLabel(order.payment_method)}
                  </p>
                  {order.change_for && (
                    <p className="text-gray-700">
                      <span className="font-medium">Troco para:</span> R$ {order.change_for.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Customer Info */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Cliente</h4>
              <div className="space-y-2 text-sm">
                <p className="text-gray-700">
                  <span className="font-medium">Nome:</span> {order.customer_name || 'Não informado'}
                </p>
                <p className="text-gray-700">
                  <span className="font-medium">Telefone:</span> {order.customer_phone || 'Não informado'}
                </p>
              </div>
            </div>

            {/* Total Section */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total do Pedido</span>
                <span className="text-2xl font-bold">
                  R$ {order.total?.toFixed(2) || '0.00'}
                </span>
              </div>
              {order.delivery_fee > 0 && (
                <p className="text-sm text-white text-opacity-90 mt-2">
                  (Inclui taxa de entrega: R$ {order.delivery_fee.toFixed(2)})
                </p>
              )}
              {order.cashback_used > 0 && (
                <p className="text-sm text-white text-opacity-90 mt-1">
                  💰 Cashback usado: R$ {order.cashback_used.toFixed(2)}
                </p>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={() => setSelectedOrderDetails(null)}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-6 rounded-xl font-semibold transition-all duration-200 active:scale-95"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  };

export default OrderDetailsModal;
